// NewReport.jsx — v161.22.24
//
// Tester picks (1) the app being tested and (2) the build installed, then
// confirms auto-detected device + iOS version. Submit creates a draft
// report and redirects to /reports/:id/edit where they add bug entries.
//
// Auto-fill priorities — minimize typing:
//   • iOS version: parsed from navigator.userAgent (regex below). Editable.
//   • Device model: NOT available from web UA on modern iOS (Apple suppresses
//     it for fingerprinting reasons). Tester picks from a curated dropdown.
//     Sticky in localStorage so they only pick once.
//   • App version: derived server-side from buildId once the report is
//     created, no client input needed.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { me, bugReports } from '../lib/api.js';

const DEVICE_KEY = 'velkra_dist_device_model';

const DEVICE_MODELS = [
  'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
  'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
  'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 mini',
  'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 mini',
  'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
  'iPhone SE (3rd gen)', 'iPhone SE (2nd gen)',
  'iPad Pro 13"', 'iPad Pro 11"', 'iPad Air', 'iPad mini', 'iPad',
  'Other'
];

function detectIOSVersion() {
  const ua = navigator.userAgent || '';
  // "iPhone; CPU iPhone OS 18_3_1 like Mac OS X"
  const m = ua.match(/OS (\d+)[._](\d+)(?:[._](\d+))?/i);
  if (!m) return '';
  return [m[1], m[2], m[3]].filter(Boolean).join('.');
}

export default function NewReport() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [appSlug, setAppSlug] = useState('');
  const [builds, setBuilds] = useState([]);
  const [buildId, setBuildId] = useState('');
  const [deviceModel, setDeviceModel] = useState(
    localStorage.getItem(DEVICE_KEY) || ''
  );
  const [iosVersion, setIosVersion] = useState(detectIOSVersion());
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    me.apps()
      .then(r => setApps(r.apps || []))
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    if (!appSlug) { setBuilds([]); setBuildId(''); return; }
    me.builds(appSlug)
      .then(r => {
        setBuilds(r.builds || []);
        // Default to the most recent build (first in list — backend orders DESC).
        if (r.builds?.length) setBuildId(r.builds[0].id);
      })
      .catch(e => setError(e.message));
  }, [appSlug]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!appSlug) { setError('Pick an app first'); return; }
    if (!buildId) { setError('Pick a build first'); return; }
    if (!deviceModel) { setError('Pick your device'); return; }
    setSubmitting(true);
    setError('');
    try {
      // Persist the device choice — same physical device, same answer next time.
      localStorage.setItem(DEVICE_KEY, deviceModel);
      const draft = await bugReports.create({
        appSlug, buildId, title: title.trim() || null,
        deviceModel, iosVersion
      });
      navigate(`/reports/${draft.id}/edit`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  if (apps === null) return <div className="loading">Loading apps…</div>;
  if (apps.length === 0) {
    return (
      <div className="empty-state">
        <h2>No apps available to test</h2>
        <p>You don't have access to any apps yet. Ask the developer to add you to a tester group.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>New Bug Report</h1>
      </header>

      {/* v161.22.29: alternative submission path */}
      <div className="card" style={{ background: 'rgba(108,92,231,0.08)', fontSize: 13, marginBottom: 16 }}>
        Prefer to draft in Word or Google Docs?
        {' '}<a href="/reports/upload-pdf" style={{ color: 'var(--accent)', fontWeight: 600 }}>Upload a PDF instead</a>.
      </div>

      <form className="form" onSubmit={handleCreate}>
        <div className="field">
          <label>App</label>
          <select value={appSlug} onChange={e => setAppSlug(e.target.value)} required>
            <option value="">— Select app —</option>
            {apps.map(a => (
              <option key={a.app_slug} value={a.app_slug}>{a.app_name || a.app_slug}</option>
            ))}
          </select>
        </div>

        {appSlug && (
          <div className="field">
            <label>Build</label>
            {builds.length === 0 ? (
              <div className="muted">No builds installed for this app yet.</div>
            ) : (
              <select value={buildId} onChange={e => setBuildId(e.target.value)} required>
                {builds.map(b => (
                  <option key={b.id} value={b.id}>
                    v{b.version} ({b.build_number}) — {b.channel || 'testing'}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="field">
          <label>Device</label>
          <select value={deviceModel} onChange={e => setDeviceModel(e.target.value)} required>
            <option value="">— Select device —</option>
            {DEVICE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <small className="muted">Sticky — only need to pick once per device.</small>
        </div>

        <div className="field">
          <label>iOS version</label>
          <input
            type="text"
            value={iosVersion}
            onChange={e => setIosVersion(e.target.value)}
            placeholder="e.g. 18.3.1"
          />
          <small className="muted">
            Auto-detected from your browser; edit if wrong.
          </small>
        </div>

        <div className="field">
          <label>Report title (optional)</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. May 18 manual testing — DOCX editor"
          />
        </div>

        {error && <div className="error">{error}</div>}

        <div className="actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Creating…' : 'Start Report'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/reports')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
